import { Modal, TextInput, Select, Stack, Text, Center, Loader, MultiSelect, Grid } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { ItemCard } from '@/components/ItemCard';
import { translate } from '@/lib';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useItemsInfinite, type ApiItem } from '@/models/Item';
import { getCategories, getRares, getGenres, getDisplayName } from '@/models/Metadata';
import { getWeapons } from '@/models/Weapon';
import { useDebouncedValue } from '@mantine/hooks';
import style from './Style.module.scss';
import clsx from 'clsx';
import { useStore } from '@/store';

interface SearchModalProps {
  opened: boolean;
  onClose: () => void;
  onItemSelect?: (item: ApiItem) => void;
}

export function SearchModal({ opened, onClose, onItemSelect }: SearchModalProps) {

  const { isIngameBrowser } = useStore();
  const [searchInput, setSearchInput] = useState('');
  const [selectedRarity, setSelectedRarity] = useState<string | null>('all');
  const [selectedCategory, setSelectedCategory] = useState<string | null>('all');
  const [selectedGenre, setSelectedGenre] = useState<string | null>('all');
  const [selectedWeapon, setSelectedWeapon] = useState<string | null>('all');
  const [selectedPower, setSelectedPower] = useState<string[]>([]);

  // Debounce search input to avoid too many API calls
  const [debouncedSearch] = useDebouncedValue(searchInput, 400);

  // Build filter object for API
  const filter = useMemo(() => {
    const f: {
      q?: string;
      rare?: number[];
      category?: number[];
      genre?: number[];
      weapon?: number[];
      power?: string[];
    } = {};

    if (debouncedSearch) {
      f.q = debouncedSearch;
    }

    if (selectedRarity && selectedRarity !== 'all') {
      f.rare = [parseInt(selectedRarity, 10)];
    }

    if (selectedCategory && selectedCategory !== 'all') {
      f.category = [parseInt(selectedCategory, 10)];
    }

    if (selectedGenre && selectedGenre !== 'all') {
      f.genre = [parseInt(selectedGenre, 10)];
    }

    if (selectedWeapon && selectedWeapon !== 'all') {
      f.weapon = [parseInt(selectedWeapon, 10)];
    }

    if (selectedPower.length > 0 && selectedCategory === '10') {
      f.power = selectedPower as string[];
    }

    return Object.keys(f).length > 0 ? f : undefined;
  }, [debouncedSearch, selectedRarity, selectedCategory, selectedGenre, selectedWeapon, selectedPower]);

  // Use infinite scroll hook with search filter - only enabled when modal is opened
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    isFetching,
  } = useItemsInfinite(undefined, filter, 30, opened);

  // Refs for infinite scroll
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  const scrollCleanupRef = useRef<(() => void) | null>(null);

  // Stable callback for fetching next page
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isFetching, fetchNextPage]);

  // Setup intersection observer
  useEffect(() => {
    if (!opened) return;

    const element = loadMoreRef.current;
    const container = resultsContainerRef.current;
    if (!element || !container) return;

    // Don't set up observer while loading initial data
    if (isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [target] = entries;
        if (target.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        root: container,
        rootMargin: '0px',
        threshold: 0.1,
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [opened, isLoading, handleLoadMore]);

  const displayItems = data?.items || [];

  const handleItemClick = (item: ApiItem) => {
    onItemSelect?.(item);
    // onClose();
  };

  // Get rarity options from API metadata - recompute when modal opens
  const rarityOptions = useMemo(() => {
    const rares = getRares();
    const options = [
      { value: 'all', label: translate('ff.search.allRarities', 'All Rarities') },
    ];

    if (rares) {
      rares.forEach(rare => {
        options.push({
          value: String(rare.id),
          label: getDisplayName(rare),
        });
      });
    }

    return options;
  }, [opened]);

  // Get category options from API metadata - recompute when modal opens
  const categoryOptions = useMemo(() => {
    const categories = getCategories();
    const options = [
      { value: 'all', label: translate('ff.search.allCategories', 'All Categories') },
    ];

    if (categories) {
      categories.forEach(category => {
        options.push({
          value: String(category.id),
          label: getDisplayName(category),
        });
      });
    }

    return options;
  }, [opened]);

  // Get genre options based on selected category - recompute when category changes
  const genreOptions = useMemo(() => {
    const genres = getGenres();
    const options = [
      { value: 'all', label: translate('ff.search.allGenres', 'All Genres') },
    ];

    if (genres && selectedCategory && selectedCategory !== 'all') {
      const categoryId = parseInt(selectedCategory, 10);
      // Filter genres by selected category
      const filteredGenres = genres.filter(genre => genre.category === categoryId);
      filteredGenres.forEach(genre => {
        options.push({
          value: String(genre.id),
          label: getDisplayName(genre),
        });
      });
    }

    return options;
  }, [opened, selectedCategory]);

  // Get weapon options based on selected genre - recompute when genre changes
  const weaponOptions = useMemo(() => {
    const weapons = getWeapons();
    const options = [
      { value: 'all', label: translate('ff.search.allWeapons', 'All Weapons') },
    ];

    if (weapons && selectedGenre && selectedGenre !== 'all') {
      const genreId = parseInt(selectedGenre, 10);
      const filtered = weapons.filter(w => w.genre === genreId);
      filtered.forEach(weapon => {
        options.push({
          value: String(weapon.id),
          label: getDisplayName(weapon),
        });
      });
    }

    return options;
  }, [opened, selectedGenre]);

  const powerOptions = [
    { value: 'damage', label: translate('ff.item.stats.damage', 'Damage') },
    { value: 'cartridge', label: translate('ff.item.stats.cartridge', 'Cartridge') },
    { value: 'biteArmor', label: translate('ff.item.stats.biteArmor', 'Bite Armor') },
    { value: 'accuracy', label: translate('ff.item.stats.accuracy', 'Accuracy') },
    { value: 'speed', label: translate('ff.item.stats.speed', 'Speed') },
    { value: 'range', label: translate('ff.item.stats.range', 'Range') },
    { value: 'reload', label: translate('ff.item.stats.reload', 'Reload') },
    { value: 'playerSpeedFactor', label: translate('ff.item.stats.playerSpeedFactor', 'Player Speed Factor') },
  ];

  // Reset genre and weapon when category changes
  const handleCategoryChange = (value: string | null) => {
    setSelectedCategory(value);
    setSelectedGenre('all');
    setSelectedWeapon('all');
  };

  // Reset weapon when genre changes
  const handleGenreChange = (value: string | null) => {
    setSelectedGenre(value);
    setSelectedWeapon('all');
  };

  // Reset filters when modal closes
  useEffect(() => {
    if (!opened) {
      setSearchInput('');
      setSelectedRarity('all');
      setSelectedCategory('all');
      setSelectedGenre('all');
      setSelectedWeapon('all');
    }
  }, [opened]);

  // Check if genre filter should be shown (only when a category is selected)
  const showGenreFilter = selectedCategory && selectedCategory !== 'all' && genreOptions.length > 1;
  // Check if weapon filter should be shown (only when a genre is selected and there are weapons)
  const showWeaponFilter = selectedGenre && selectedGenre !== 'all' && weaponOptions.length > 1;
  const lastScrollTop = useRef(0);
  const [scrollDirection, setScrollDirection] = useState<'none' | 'up' | 'down'>('none');

  // Callback ref: attach scroll listener when the results container is mounted (avoids null ref when modal just opened)
  const setResultsContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (scrollCleanupRef.current) {
        scrollCleanupRef.current();
        scrollCleanupRef.current = null;
      }
      (resultsContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (!node) {
        setScrollDirection('none');
        return;
      }
      if (opened) {
        const handleScroll = () => {
          const currentScrollTop = node.scrollTop;
          if (currentScrollTop > lastScrollTop.current) {
            setScrollDirection('down');
          } else if (currentScrollTop < lastScrollTop.current) {
            setScrollDirection('up');
          }
          lastScrollTop.current = currentScrollTop <= 0 ? 0 : currentScrollTop;
        };
        node.addEventListener('scroll', handleScroll);
        scrollCleanupRef.current = () => node.removeEventListener('scroll', handleScroll);
      }
    },
    [opened]
  );


  console.log(selectedCategory)

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={translate('common.search.search', 'Search...')}
      size="xl"
      fullScreen
      classNames={{ body: style.modalBody }}
    >
      <Stack gap="md">
        <Stack className={clsx(style.wrapFilter, scrollDirection === 'up' && style.wrapFilterUp, scrollDirection === 'down' && style.wrapFilterDown)}>
          <TextInput
            placeholder={translate('ff.search.placeholder', 'Search items...')}
            leftSection={<IconSearch size={16} />}
            value={searchInput}
            onChange={(e) => setSearchInput(e.currentTarget.value)}
            size="md"
          />

          <Grid >
            <Grid.Col span={{ base: 6, xs: 4 }}>
              <Select
                placeholder={translate('ff.search.filterByCategory', 'Filter by Category')}
                data={categoryOptions}
                value={selectedCategory}
                onChange={handleCategoryChange}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 6, xs: 4 }}>
              <Select
                placeholder={translate('ff.search.filterByRarity', 'Filter by Rarity')}
                data={rarityOptions}
                value={selectedRarity}
                onChange={setSelectedRarity}
              />
            </Grid.Col>

            {/* Genre + Weapon filters - same row */}
            {showGenreFilter && (
              <>
                <Grid.Col span={{ base: 6, xs: 4 }}>
                  <Select
                    placeholder={translate('ff.search.filterByGenre', 'Filter by Genre')}
                    data={genreOptions}
                    value={selectedGenre}
                    onChange={handleGenreChange}
                  />
                </Grid.Col>
                {showWeaponFilter && (
                  <Grid.Col span={{ base: 6, xs: 4 }}>
                    <Select
                      placeholder={translate('ff.search.filterByWeapon', 'Filter by Weapon')}
                      data={weaponOptions}
                      value={selectedWeapon}
                      onChange={setSelectedWeapon}
                    />
                  </Grid.Col>
                )}
              </>
            )}


            {selectedCategory === '10' && (
              <Grid.Col span={{ base: 6, xs: 4 }}>
                <MultiSelect
                  placeholder={translate('ff.search.filterByPower', 'Filter by Power')}
                  data={powerOptions}
                  value={selectedPower}
                  multiple
                  maxValues={2}
                  onChange={(value) => setSelectedPower(value as string[])}
                />
              </Grid.Col>
            )}
          </Grid>

          <Text size="sm" c="dimmed">
            {displayItems.length} {translate('ff.search.results', 'results')}
            {hasNextPage && '+'}
          </Text>
        </Stack>

        <div className={style.resultsContainer} ref={setResultsContainerRef}>
          {isLoading && (
            <Center py="xl">
              <Loader size="lg" />
            </Center>
          )}

          {isError && (
            <Center py="xl">
              <Text c="dimmed">{translate('ff.error.loadFailed', 'Failed to load items')}</Text>
            </Center>
          )}

          {!isLoading && !isError && displayItems.length > 0 && (
            <Stack gap="0" className={isIngameBrowser ? 'itemContainerRow' : ''}>
              {displayItems.map((item) => (
                <ItemCard key={item.id} item={item} onClick={handleItemClick} />
              ))}

              {/* Load more trigger - only visible when there's more to load */}
              {hasNextPage && (
                <div ref={loadMoreRef} style={{ height: 20, width: '100%' }}>
                  {isFetchingNextPage && (
                    <Center>
                      <Loader size="sm" />
                    </Center>
                  )}
                </div>
              )}

              {!hasNextPage && (
                <Center py="md">
                  <Text size="sm" c="dimmed">{translate('ff.list.noMore', 'No more items')}</Text>
                </Center>
              )}
            </Stack>
          )}

          {!isLoading && !isError && displayItems.length === 0 && (
            <Text ta="center" c="dimmed" py="xl">
              {translate('common.search.noResult', 'No results...')}
            </Text>
          )}
        </div>
      </Stack>
    </Modal>
  );
}
